import { useEffect } from 'react'
import { RoomEvent, Track, type Room, type RemoteParticipant } from 'livekit-client'
import { getSocket } from '../services/socket'
import { formatPhoneDisplay } from '../utils/formatPhone'
import { useAuthStore } from '../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'

const CHUNK_INTERVAL_MS = 250
const HEALTH_MS = 3000

// `phone:+19255550100` (invite-by-phone / dial-in SIP participants get
// this identity from telephony call-actions) → `19255550100`. `sip_*`
// identities carry no number — those legs are skipped (no way to mint
// the canonical archive-safe speaker id for them).
function phoneDigitsFromIdentity(identity: string): string | null {
  if (!identity.startsWith('phone:')) return null
  const digits = identity.slice('phone:'.length).replace(/\D/g, '')
  return /^\d{7,15}$/.test(digits) ? digits : null
}

// OSPROD-184: elected feeder = earliest (joinedAt, userId) among the
// channel's voice states. VoiceState rows exist only for real users, so
// guests and phone legs can never win. EXACTLY mirrors the server-side
// gate in socket.ts transcript:start — the server is the referee, this
// election only decides who bothers to arm a recorder.
function electedFeederId(channelId: string): string | null {
  let best: { joinedAt: string; userId: string } | null = null
  for (const server of useServerStore.getState().servers) {
    for (const cat of server.categories ?? []) {
      for (const ch of cat.channels ?? []) {
        if (ch.id !== channelId) continue
        const states = (ch as { voiceStates?: { userId: string; joinedAt: string }[] }).voiceStates ?? []
        for (const vs of states) {
          if (
            !best
            || vs.joinedAt < best.joinedAt
            || (vs.joinedAt === best.joinedAt && vs.userId < best.userId)
          ) {
            best = { joinedAt: vs.joinedAt, userId: vs.userId }
          }
        }
      }
    }
  }
  return best?.userId ?? null
}

interface Leg {
  recorder: MediaRecorder | null
  cloned: MediaStreamTrack | null
  /** the upstream track the clone was made from — SIP re-INVITE replaces
   *  it while the stale clone stays readyState='live', so identity (not
   *  liveness) is the churn signal. */
  srcTrack: MediaStreamTrack | null
  started: boolean
  unsupported: boolean
}

/**
 * OSPROD-184: room-scope PSTN phone-leg transcription feeder.
 *
 * A phone participant (Invite by Phone / direct dial-in) has no client of
 * its own, so SOMEBODY in the room must record its remote audio track and
 * stream it to the server as an extra transcript stream (same mechanism
 * as the DM-call useRemotePhoneTranscription, but per phone participant
 * and with an elected feeder instead of a call owner).
 *
 * Election: earliest (VoiceState.joinedAt, userId) in the channel — the
 * server enforces the same rule, so a double-arm during membership churn
 * resolves to a single accepted stream. Off-rail rooms (hidden
 * `_personal_rooms` meetings) have no serverStore voice-state mirror;
 * we fall back to LiveKit join order there and let the server referee.
 *
 * NOT a mediaManager surface: clones REMOTE subscribed tracks for local
 * MediaRecorders — no local capture, no getUserMedia, no publish.
 */
export function useRoomPhoneTranscription(channelId: string | null | undefined, room: Room | null) {
  const myUserId = useAuthStore((s) => s.user?.id)

  useEffect(() => {
    // Guests never feed (the server rejects them) — guest sessions have
    // no authStore.user, so myUserId is already the right gate.
    if (!channelId || !room || !myUserId) return
    const socket = getSocket()
    const legs = new Map<string, Leg>() // speakerId → recorder leg
    let disposed = false

    const amFeeder = (): boolean => {
      const elected = electedFeederId(channelId)
      if (elected) return elected === myUserId
      // Fallback for rooms without a serverStore mirror: LiveKit join
      // order over real users (identity = db userId; guests are
      // `guest_*`, PSTN legs `phone:*`/`sip_*`).
      const candidates: { t: number; id: string }[] = []
      const consider = (identity: string, joinedAt: Date | undefined) => {
        if (
          identity.startsWith('phone:')
          || identity.startsWith('sip_')
          || identity.startsWith('guest_')
        ) return
        candidates.push({ t: joinedAt?.getTime() ?? 0, id: identity })
      }
      consider(room.localParticipant.identity, room.localParticipant.joinedAt)
      for (const p of room.remoteParticipants.values()) consider(p.identity, p.joinedAt)
      candidates.sort((a, b) => (a.t - b.t) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      return candidates[0]?.id === myUserId
    }

    const stopLeg = (speakerId: string, emitStop: boolean) => {
      const leg = legs.get(speakerId)
      if (!leg) return
      if (leg.recorder && leg.recorder.state !== 'inactive') {
        try { leg.recorder.stop() } catch { /* noop */ }
      }
      if (leg.cloned) { try { leg.cloned.stop() } catch { /* noop */ } }
      const wasStarted = leg.started
      legs.delete(speakerId)
      if (emitStop && wasStarted) {
        try {
          socket.emit('transcript:stop', { channelId, remote: true, remoteSpeakerId: speakerId })
        } catch { /* noop */ }
      }
    }

    const stopAll = (emitStop: boolean) => {
      for (const id of [...legs.keys()]) stopLeg(id, emitStop)
    }

    const micTrackOf = (p: RemoteParticipant): MediaStreamTrack | null => {
      const pub = p.getTrackPublication(Track.Source.Microphone)
      const t = pub?.track?.mediaStreamTrack
      return t && t.readyState === 'live' ? t : null
    }

    const startLeg = (speakerId: string, displayNumber: string, src: MediaStreamTrack) => {
      let cloned: MediaStreamTrack
      try {
        cloned = src.clone()
      } catch (err) {
        console.warn('[RoomPhoneTx] track.clone failed:', err)
        return
      }
      let rec: MediaRecorder
      try {
        rec = new MediaRecorder(new MediaStream([cloned]), { mimeType: 'audio/webm;codecs=opus' })
      } catch (err) {
        // Latch per speaker so the health loop doesn't retry a recorder
        // the platform fundamentally can't build.
        console.warn('[RoomPhoneTx] MediaRecorder unsupported:', err)
        legs.set(speakerId, { recorder: null, cloned: null, srcTrack: null, started: false, unsupported: true })
        try { cloned.stop() } catch { /* noop */ }
        return
      }
      // Idempotent server-side: re-emitting start resets that speaker's
      // Deepgram session.
      socket.emit('transcript:start', {
        channelId,
        remoteSpeaker: { id: speakerId, name: formatPhoneDisplay(displayNumber) },
      })
      rec.ondataavailable = async (event) => {
        if (disposed) return
        if (!event.data || event.data.size === 0) return
        try {
          const buf = await event.data.arrayBuffer()
          // volatile: drop (never queue) chunks while the socket is down —
          // socket.io's unbounded sendBuffer ate gigabytes on half-dead
          // clients (2026-08-06). See useTranscription.ts.
          socket.volatile.emit('transcript:audio-chunk', {
            channelId,
            chunk: buf,
            remote: true,
            remoteSpeakerId: speakerId,
          })
        } catch (err) {
          console.warn('[RoomPhoneTx] chunk forward failed:', err)
        }
      }
      try {
        rec.start(CHUNK_INTERVAL_MS)
      } catch (err) {
        console.warn('[RoomPhoneTx] recorder.start failed:', err)
        try { cloned.stop() } catch { /* noop */ }
        try {
          socket.emit('transcript:stop', { channelId, remote: true, remoteSpeakerId: speakerId })
        } catch { /* noop */ }
        return
      }
      legs.set(speakerId, { recorder: rec, cloned, srcTrack: src, started: true, unsupported: false })
      console.info(`[RoomPhoneTx] recorder started (channel=${channelId}, speaker=${speakerId})`)
    }

    // Reconcile recorders against the room's current phone participants.
    // Covers: election changes (feeder left → we won / we lost), phone
    // legs joining/leaving, SIP re-INVITE track swaps (srcTrack identity
    // change), and recorder death (health loop).
    const sync = () => {
      if (disposed) return
      if (!amFeeder()) {
        stopAll(true)
        return
      }
      const present = new Map<string, { num: string; track: MediaStreamTrack | null }>()
      for (const p of room.remoteParticipants.values()) {
        const digits = phoneDigitsFromIdentity(p.identity)
        if (!digits) continue
        present.set(`phone_${digits}`, { num: `+${digits}`, track: micTrackOf(p) })
      }
      for (const [speakerId, leg] of [...legs]) {
        const now = present.get(speakerId)
        const stale =
          !now
          || (leg.started && (
            !now.track
            || now.track !== leg.srcTrack
            || !leg.recorder
            || leg.recorder.state !== 'recording'
            || !leg.cloned
            || leg.cloned.readyState !== 'live'
          ))
        if (stale) stopLeg(speakerId, true)
      }
      for (const [speakerId, info] of present) {
        if (!info.track) continue
        const leg = legs.get(speakerId)
        if (leg?.started || leg?.unsupported) continue
        startLeg(speakerId, info.num, info.track)
      }
    }

    sync()
    const onChange = () => sync()
    room.on(RoomEvent.TrackSubscribed, onChange)
    room.on(RoomEvent.TrackUnsubscribed, onChange)
    room.on(RoomEvent.ParticipantConnected, onChange)
    room.on(RoomEvent.ParticipantDisconnected, onChange)
    // Election input lives in serverStore (voiceStates with joinedAt) —
    // membership churn there can flip the feeder without any LiveKit
    // event on OUR room object.
    const unsubServers = typeof useServerStore.subscribe === 'function'
      ? useServerStore.subscribe(onChange)
      : null
    const health = window.setInterval(sync, HEALTH_MS)

    return () => {
      disposed = true
      window.clearInterval(health)
      if (unsubServers) unsubServers()
      room.off(RoomEvent.TrackSubscribed, onChange)
      room.off(RoomEvent.TrackUnsubscribed, onChange)
      room.off(RoomEvent.ParticipantConnected, onChange)
      room.off(RoomEvent.ParticipantDisconnected, onChange)
      stopAll(true)
    }
  }, [channelId, room, myUserId])
}
